<template>
    <div class="payouts-page">
        <HeaderBar section-label="Approval" />
        <div id="full-container">
        <div class="loading-screen" v-if="!callReturned">
            <span class="loading-text">Plan Loading....</span>
            <div class="lds-dual-ring"></div>
        </div>
        <div class="error-window" v-else-if="gotLoad404">
            <span class="error-title">Plan not found</span>
            <span class="error-subtitle">Couldn't find a plan with code {{ planKey }} in database. If you're sure that code is correct, Carter can help get everything sorted out!</span>
        </div>
        <div class="error-window" v-else-if="gotLoad500">
            <span class="error-title">Server Error</span>
            <span class="error-subtitle">The server encountered an error trying to retrieve that plan. Try refreshing the page—if that doesn't work, please tell Carter about the error!</span>
        </div>
        <template v-else>
            <h1 id="top-text">Discounts</h1>
            <div id="window">
                <div class="row-holder" v-if="treatmentDiscount.isActive">
                    <div id="top-row">
                        <h2 class="top-description" style="font-size: 1.3em;">View and manage your discounts.</h2>
                    </div>
                    <div id="top-row">
                        <h3 class="section-title">Treatment Discounts</h3>
                    </div>
                    <DiscountRow
                        :key="treatmentDiscount.id"
                        :id="treatmentDiscount.id"
                        :display-only="true"
                    />
                </div>
                <div class="row-holder" v-if="otherDiscounts.length > 0">
                    <div id="top-row">
                        <h3 class="section-title">Other Discounts</h3>
                    </div>
                    <DiscountRow
                        v-for="discount in otherDiscounts"
                        :key="discount.id"
                        :id="discount.id"
                        :other-discount="discount"
                        :display-only="true"
                        @remove="removeOtherDiscount(discount.id)"
                    />
                </div>
                <div class="btn-container">
                    <button class="add-btn hover-grow" @click="showAddOtherModal = true">+ Add other discounts</button>
                </div>
                <div class="plan-notes" v-if="planNote">
                    <h3>Plan Notes</h3>
                    <p>{{ planNote }}</p>
                </div>
            </div>
            <transition name="fade" mode="out-in">
                <div class="modal-overlay" v-if="showAddOtherModal" @click.self="showAddOtherModal = false">
                    <AddDiscountModal
                        :isOtherDiscount="true"
                        @click.stop 
                        @exitModal="showAddOtherModal = false"
                        @addDiscount="onAddOtherDiscount"
                    />
                </div>
            </transition>
        </template>
        </div>
    </div>
</template>

<script>
import HeaderBar from '../Onboarding/components/HeaderBar.vue';
import DiscountRow from '../Onboarding/components/DiscountRow.vue';
import AddDiscountModal from '../Onboarding/components/modals/AddDiscountModal.vue';
import { mapActions } from 'vuex';
import { GetPlanFromDB } from '../Onboarding/utils/functions.js';

export default {
    name: 'PayoutsDiscounts',
    components: {
        HeaderBar,
        DiscountRow,
        AddDiscountModal,
    },
    props: {
        planKey: String,
    },
    data() {
        return {
            callReturned: false,
            gotLoad404: false,
            gotLoad500: false,
            localPlan: null,
            showAddOtherModal: false,
        };
    },
    computed: {
        treatmentDiscount() {
            let discount = this.$store.state.treatmentDiscounts[0]
            if(discount && discount.isActive) {
                return discount;
            }
            else {
                return null;
            }
        },
        otherDiscounts() {
            return this.$store.state.otherDiscounts || [];
        },
        planNote() {
            return this.$store.state.planNote || '';
        },
    },
    methods: {
        ...mapActions(['setEverything', 'addOtherDiscountFromPayload', 'removeOtherDiscount']),
        async LoadPlan() {
            const returnData = await GetPlanFromDB(this.planKey);
            if (returnData === 404) {
                this.gotLoad404 = true;
                this.callReturned = true;
                return;
            }
            if (returnData === 500) {
                this.callReturned = true;
                this.gotLoad500 = true;
                return;
            }
            this.localPlan = JSON.parse(returnData.plans);
            this.setEverything(this.localPlan);
            this.callReturned = true;
        },
        onAddOtherDiscount(payload) {
            this.addOtherDiscountFromPayload(payload);
            this.showAddOtherModal = false;
        },
    },
    mounted() {
        this.LoadPlan();
    },
};
</script>

<style scoped>
.payouts-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

#full-container {
    width: 100%;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    margin: 0;
    background-color: var(--color-teal-background);
    padding: 2% 0;
    box-sizing: border-box;
}

#top-text {
    margin: 0;
    margin-top: 1%;
    margin-bottom: 1%;
    color: var(--font-color-teal);
}

#window {
    width: 90vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    background-color: white;
    border-radius: 20px;
    padding-bottom: 1.5em;
}

.row-holder {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
}

#top-row {
    display: flex;
    width: 100%;
    justify-content: space-between;
}

#top-row h2 {
    margin-left: 2em;
    font-size: 1.5em;
}

.section-title {
    color: var(--color-dark-gray);
    padding-left: 1.5%;
    margin: 0.5em 0;
    margin-left: 2em;
}

.add-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid var(--color-light-blue);
    border-radius: 40px;
    height: 4em;
    min-width: 220px;
    background-color: white;
    color: var(--color-bright-blue);
    font-size: 1.15em;
    font-weight: 600;
    padding: 0 1.2em;
    cursor: pointer;
    margin-top: 0.5em;
}

.btn-container {
    margin-top: 0.5em;
    display: flex;
    justify-content: center;
    width: 97%;
    margin-bottom: 0.5em;
}

.loading-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 40vh;
    gap: 1em;
}

.loading-text {
    color: var(--font-color-teal);
    font-weight: 600;
}

.lds-dual-ring {
    display: inline-block;
    width: 48px;
    height: 48px;
}

.lds-dual-ring::after {
    content: " ";
    display: block;
    width: 40px;
    height: 40px;
    margin: 4px;
    border-radius: 50%;
    border: 3px solid var(--color-bright-blue);
    border-color: var(--color-bright-blue) transparent var(--color-bright-blue) transparent;
    animation: lds-dual-ring 1.2s linear infinite;
}

@keyframes lds-dual-ring {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.error-window {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2em;
    max-width: 500px;
    background-color: white;
    border-radius: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.error-title {
    font-size: 1.4em;
    font-weight: 700;
    color: var(--color-dark-blue);
    margin-bottom: 0.5em;
}

.error-subtitle {
    color: var(--color-dark-gray);
    font-size: 0.95em;
    line-height: 1.4;
}

.modal-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 98;
    background-color: rgba(0, 0, 0, 0.2);
}

.plan-notes {
    padding: 1em;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 30%;
    border: 3px solid var(--color-medium-gray);
    box-shadow: 3px 3px 10px var(--color-medium-gray-shadow);
    word-wrap: break-word;
    border-radius: 15px;
}

.plan-notes h3 {
    margin-top: 0;
    margin-bottom: 0.5em;
    color: var(--color-dark-gray);
    font-size: 1.2em;
    font-weight: 600;
}

.plan-notes p {
    margin: 0;
    color: var(--color-dark-gray);
    font-size: 0.95em;
    line-height: 1.5;
    white-space: pre-wrap;
}
</style>
